import { ScheduleAiFormSuggestionPicker } from "@/features/ai-builder/ScheduleAiFormSuggestionPicker";
import { useDraftScheduleWithAi } from "@/features/ai-builder/useDraftScheduleWithAi";
import { type ScheduleAiWorkspace, useScheduleAiDraftStore } from "@/features/ai-builder/useScheduleAiDraftStore";
import type { ScheduleDraft } from "@/features/ai-builder/scheduleDraft";
import { useSelectFormsLite } from "@/features/forms/api/selectForms";
import { useCreateScheduleFromDraft } from "@/features/schedules/api/createScheduleFromDraft";
import ScheduleForm from "@/features/schedules/components/ScheduleForm";
import type { ScheduleSchemaT } from "@/features/schedules/types/scheduleSchema";
import { useNav } from "@/utils/nav";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Badge } from "@patient-watch/ui/badge";
import { Button } from "@patient-watch/ui/button";
import { Loader2, Sparkles, Trash2 } from "lucide-react";
import * as React from "react";

function draftToFormValues(draft: ScheduleDraft): ScheduleSchemaT {
    return {
        schedule_id: crypto.randomUUID(),
        schedule_name: draft.schedule_name,
        schedule_description: draft.schedule_description,
        is_published: false,
        timings: draft.timings.map((timing) => ({ ...timing, timing_id: crypto.randomUUID() })),
    };
}

function formValuesToDraft(values: ScheduleSchemaT, suggestedForms: ScheduleDraft["suggested_forms"]): ScheduleDraft {
    return {
        schedule_name: values.schedule_name,
        schedule_description: values.schedule_description ?? "",
        timings: values.timings.map(({ timing_id: _timingId, ...timing }) => timing),
        suggested_forms: suggestedForms,
    };
}

function NewAiSchedulePage() {
    const navigate = useNavigate();
    const formsQuery = useSelectFormsLite();
    const createFromDraft = useCreateScheduleFromDraft();
    const draftAi = useDraftScheduleWithAi();
    const [workspace, setWorkspace] = React.useState<ScheduleAiWorkspace | null>(() =>
        useScheduleAiDraftStore.getState().takeWorkspace()
    );
    const [editorRevision, setEditorRevision] = React.useState(0);
    const [error, setError] = React.useState<string | null>(null);

    useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Schedules", route: { to: "/builder/schedules" } },
        {
            label: workspace?.draft.schedule_name ?? "New AI draft",
            route: { to: "/builder/schedules/new" },
            status: "unsaved",
        },
    ]);

    React.useEffect(() => {
        if (!workspace) void navigate({ to: "/builder/schedules", replace: true });
    }, [navigate, workspace]);

    const formValues = React.useMemo(
        () => (workspace ? draftToFormValues(workspace.draft) : null),
        [editorRevision, workspace]
    );

    if (!workspace || !formValues) return null;

    const availableForms = (formsQuery.data ?? []).flatMap((form) =>
        form.form_id && form.form_name
            ? [
                  {
                      form_id: form.form_id,
                      form_name: form.form_name,
                      slug: form.slug ?? undefined,
                      prevalence: form.prevalence ?? undefined,
                      orthopaedic_areas: form.orthopaedic_areas ?? undefined,
                      is_published: form.is_published ?? undefined,
                  },
              ]
            : []
    );

    const updateWorkspace = (update: Partial<ScheduleAiWorkspace>) => {
        setWorkspace((current) => (current ? { ...current, ...update } : current));
        setError(null);
    };

    const handleRetry = async () => {
        if (formsQuery.isPending || formsQuery.isError) {
            const result = await formsQuery.refetch();
            if (result.isError) {
                setError("Could not load the available forms. Try again.");
                return;
            }
        }

        draftAi.mutate(
            { prompt: workspace.prompt },
            {
                onSuccess: (result) => {
                    updateWorkspace({ draft: result.draft, warnings: result.warnings });
                    setEditorRevision((revision) => revision + 1);
                },
                onError: (caught) => {
                    setError(caught instanceof Error ? caught.message : "Could not draft the schedule. Try again.");
                },
            }
        );
    };

    const handleSave = async (values: ScheduleSchemaT) => {
        setError(null);
        try {
            const result = await createFromDraft.mutateAsync({
                draft: formValuesToDraft(values, workspace.draft.suggested_forms),
            });
            void navigate({
                to: "/builder/schedules/$scheduleId",
                params: { scheduleId: result.scheduleId },
            });
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : "Could not save the draft.");
        }
    };

    const handleSuggestionChange = (index: number, formId: string) => {
        const selected = availableForms.find((form) => form.form_id === formId);
        const suggestedForms = workspace.draft.suggested_forms.map((suggestion, suggestionIndex) =>
            suggestionIndex === index
                ? {
                      form_id: selected?.form_id ?? null,
                      form_name: selected?.form_name ?? suggestion.form_name,
                      needs_pick: !selected,
                  }
                : suggestion
        );
        updateWorkspace({ draft: { ...workspace.draft, suggested_forms: suggestedForms } });
    };

    return (
        <div className="min-h-screen p-6">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                        {workspace.draft.schedule_name}
                    </h1>
                    <Badge variant="outline">Not saved</Badge>
                </div>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={createFromDraft.isPending || draftAi.isPending}
                    onClick={() => void navigate({ to: "/builder/schedules" })}
                >
                    <Trash2 className="h-4 w-4" />
                    Discard draft
                </Button>
            </div>

            <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
                <div className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
                    <ScheduleForm
                        key={editorRevision}
                        initialValues={formValues}
                        onSubmit={handleSave}
                        submitLabel="Save draft"
                    />
                </div>

                <aside className="space-y-4">
                    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                        <div>
                            <h2 className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                                <Sparkles className="h-4 w-4" /> AI draft
                            </h2>
                            <p className="mt-1 text-xs text-muted-foreground">
                                Retrying replaces the fields in the editor. Nothing is stored until Save draft.
                            </p>
                        </div>
                        <label className="block space-y-1.5 text-sm font-medium" htmlFor="schedule-ai-prompt">
                            Prompt
                            <textarea
                                id="schedule-ai-prompt"
                                value={workspace.prompt}
                                maxLength={2000}
                                disabled={draftAi.isPending || createFromDraft.isPending}
                                onChange={(event) => updateWorkspace({ prompt: event.target.value })}
                                className="min-h-28 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-normal"
                            />
                        </label>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!workspace.prompt.trim() || draftAi.isPending || createFromDraft.isPending}
                            onClick={handleRetry}
                        >
                            {draftAi.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            {draftAi.isPending ? "Creating draft..." : "Retry prompt"}
                        </Button>
                    </div>

                    {error ? (
                        <p
                            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                            role="alert"
                        >
                            {error}
                        </p>
                    ) : null}

                    {workspace.warnings.length ? (
                        <ul className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                            {workspace.warnings.map((warning) => (
                                <li key={warning}>{warning}</li>
                            ))}
                        </ul>
                    ) : null}

                    {workspace.draft.suggested_forms.length ? (
                        <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <div>
                                <h2 className="font-medium text-slate-900 dark:text-slate-100">
                                    Suggested questionnaires
                                </h2>
                                <p className="mt-1 text-xs text-muted-foreground">
                                    These are recommendations for template composition later. They are not saved on this
                                    schedule.
                                </p>
                            </div>
                            {formsQuery.isError ? (
                                <Button type="button" size="sm" variant="outline" onClick={() => formsQuery.refetch()}>
                                    Retry questionnaire catalogue
                                </Button>
                            ) : (
                                workspace.draft.suggested_forms.map((suggestion, index) => (
                                    <ScheduleAiFormSuggestionPicker
                                        key={`${suggestion.form_name}-${index}`}
                                        testId={`schedule-form-suggestion-${index}`}
                                        suggestion={suggestion}
                                        options={availableForms}
                                        disabled={formsQuery.isPending}
                                        onChange={(formId) => handleSuggestionChange(index, formId)}
                                    />
                                ))
                            )}
                        </div>
                    ) : null}
                </aside>
            </div>
        </div>
    );
}

export const Route = createFileRoute("/_app/builder/schedules/new")({ component: NewAiSchedulePage });
